/**
 * Shared vocabulary + pure transform for the reusable list-page pattern (search + view toggle + sort +
 * filter). The stateful part lives in `useListControls` (URL-synced); this module is deliberately pure so
 * the filter/sort logic is trivially testable and framework-free, and so any list page derives its
 * visible rows the same way.
 */

export type ViewMode = 'card' | 'list';

export type SortDir = 'asc' | 'desc';

export interface SortState<SortKey extends string> {
  key: SortKey;
  dir: SortDir;
}

export interface ListControlsState<SortKey extends string, FilterKey extends string> {
  query: string;
  view: ViewMode;
  sort: SortState<SortKey>;
  /** Active filter values keyed by filter id; `null`/absent means "no filter" for that key. */
  filters: Partial<Record<FilterKey, string | null>>;
}

/** Per-page config that teaches {@link filterSortItems} how to search, sort, and filter `T`. */
export interface ListDeriveConfig<T, SortKey extends string, FilterKey extends string> {
  /**
   * The row's searchable text for the search box (matched case-insensitively, substring). Return an
   * array to match each field independently, so a query has to land within a SINGLE field and can't
   * straddle a field boundary; a joined string does the opposite (a query can span two fields). A plain
   * string is treated as one field.
   */
  searchText: (item: T) => string | readonly string[];
  /** Comparator per sort key, always written ascending — direction is applied by {@link filterSortItems}. */
  comparators: Record<SortKey, (a: T, b: T) => number>;
  /** Predicate per filter key — returns true to KEEP the row for the given active value. */
  filterPredicates: Record<FilterKey, (item: T, value: string) => boolean>;
}

/** True when a filter value should actually be applied (non-null, non-empty). */
function isActive(value: string | null | undefined): value is string {
  return value != null && value !== '';
}

/**
 * Apply the current controls to a fully-loaded array: filter → search → sort. Pure and stable ordering
 * (the sort is direction-flipped from the ascending comparator, never re-derived per direction).
 */
export function filterSortItems<T, SortKey extends string, FilterKey extends string>(
  items: readonly T[],
  // `view` is deliberately excluded — how the list is rendered never changes which rows are shown, so a
  // caller must not have to pass it (and memoizing on it would recompute the list on a pointless view toggle).
  state: Pick<ListControlsState<SortKey, FilterKey>, 'query' | 'sort' | 'filters'>,
  config: ListDeriveConfig<T, SortKey, FilterKey>,
): T[] {
  const activeFilters = (Object.entries(state.filters) as [FilterKey, string | null | undefined][]).filter(
    ([, value]) => isActive(value),
  ) as [FilterKey, string][];

  const query = state.query.trim().toLowerCase();

  const filtered = items.filter((item) => {
    for (const [key, value] of activeFilters) {
      if (!config.filterPredicates[key](item, value)) return false;
    }
    if (query) {
      const haystack = config.searchText(item);
      const fields = typeof haystack === 'string' ? [haystack] : haystack;
      // Per field, short-circuiting: the query must fall inside one field (never across a boundary), and
      // the row bails on the first field that contains it.
      if (!fields.some((field) => field.toLowerCase().includes(query))) return false;
    }
    return true;
  });

  const compare = config.comparators[state.sort.key];
  const dir = state.sort.dir === 'desc' ? -1 : 1;
  // Copy before sorting — never mutate the caller's array (it's usually query cache data).
  // No tiebreak: `sort` is stable, so rows the comparator ranks equal keep the order the server sent.
  return [...filtered].sort((a, b) => dir * compare(a, b));
}

/** Count of filters currently applied — drives the "Filters (n)" affordance in the toolbar. */
export function activeFilterCount<FilterKey extends string>(
  filters: Partial<Record<FilterKey, string | null>>,
): number {
  return Object.values(filters).filter((v) => isActive(v as string | null | undefined)).length;
}

/** A multi-select filter with nothing chosen. Distinct from absent, which means the group's defaults. */
export const MULTI_FILTER_NONE = 'none';

/**
 * A multi-select filter's chosen values (`FilterGroup.defaultValues`). It rides the URL as ONE filter
 * value — the values joined by commas, or {@link MULTI_FILTER_NONE} — so `useListControls` needs no
 * new state; absent (or empty) means the group's defaults.
 */
export function parseMultiFilter(value: string | null | undefined, defaults: readonly string[]): string[] {
  if (value == null || value === '') return [...defaults];
  if (value === MULTI_FILTER_NONE) return [];
  return value.split(',').filter((v) => v !== '');
}

/** The URL value for a multi-select choice: null when it is exactly the defaults, so the URL stays clean. */
export function encodeMultiFilter(selected: readonly string[], defaults: readonly string[]): string | null {
  const isDefault = selected.length === defaults.length && defaults.every((d) => selected.includes(d));
  if (isDefault) return null;
  return selected.length === 0 ? MULTI_FILTER_NONE : selected.join(',');
}
